import React, { useState, useRef, useEffect, type Dispatch, type SetStateAction } from 'react'
import { AnimatePresence } from 'framer-motion'
import { Plus, Trash2, Package, ChevronDown, ChevronRight } from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import { lineTotal, isValidTiers, type PricingType, type PricingTier } from '@/lib/totals'
import { ProductPicker } from '@/components/payments/ProductPicker'
import Combobox from '@/components/Combobox'   // default export; matches JobLineItemsEditor's import

/**
 * Shared line-item editor for InvoiceDetail, QuoteDetail, and (Piece 3b-ii)
 * the upcoming RecurringInvoiceDetail series-template editor.
 *
 * Extracted verbatim from InvoiceDetail.tsx / QuoteDetail.tsx — the inlined
 * editors were byte-identical except for how each parent derives isLocked
 * (paid/void for invoice; invoiced/declined/won for quote). Behavior MUST
 * be preserved character-for-character; the only API change is the parent
 * passes `isLocked` as a prop and the component owns the ProductPicker render.
 *
 * State flow: `items` + `onItemsChange` mirror useState<LineItem[]>'s
 * Dispatch<SetStateAction<LineItem[]>> signature, so parents can pass
 * setLineItems directly and functional-update semantics are preserved
 * (handlers internally call onItemsChange(prev => ...) — byte-identical
 * to the old setLineItems(prev => ...)).
 *
 * The component manages its OWN showPicker UI state (neither parent ever
 * read it from outside the editor region). The `currency` prop is reserved
 * for future use (recurring may eventually pass workspace currency); both
 * current call sites omit it, and formatCurrency is called with no arg —
 * exactly as the inlined editors did.
 */

export interface LineItem {
  id: string
  product_id?: string
  name: string
  description: string
  qty: number
  unit: string
  unit_price: number
  total: number
  // Arc B Slice 2 — per-line pricing. Missing/'flat' → qty × unit_price (CP2 adds
  // the editor UI). tiers used only when pricing_type is 'tiered'|'volume'.
  pricing_type?: PricingType | null
  tiers?: PricingTier[] | null
  // Arc B Slice 3 — per-line modifiers: total = base × (multiplier ?? 1) + (fee ?? 0).
  line_fee?: number | null
  price_multiplier?: number | null
}

interface Props {
  items: LineItem[]
  onItemsChange: Dispatch<SetStateAction<LineItem[]>>
  isLocked: boolean
  workspaceId: string
  /** Reserved for future use (e.g. workspace currency). Currently unused —
   *  formatCurrency is called with no arg to byte-match the existing editors. */
  currency?: string
}

// Preset unit suggestions for the combobox. Suggestions only — any typed value
// is kept (allowFreeform). Customer-billing labels, NOT a pricing mode.
const UNIT_PRESETS = ['each', 'hr', 'sq ft', 'flat', 'visit', 'month', 'job', 'lb']

// Per-row unit field — freeform typeahead over UNIT_PRESETS via the shared
// Combobox. Single write path = commit-on-blur (mirrors the JobLineItemsEditor
// library-typeahead pattern): local state mirrors keystrokes; the line item is
// written only on blur, deduped against the initial value. A preset click just
// mirrors the label into local; the onBlur commit is the one and only write.
function UnitCell({ item, isLocked, onUnitChange }: {
  item: LineItem
  isLocked: boolean
  onUnitChange: (itemId: string, value: string) => void
}) {
  const [local, setLocal] = useState(item.unit)
  const initialRef = useRef(item.unit)
  const justSelectedRef = useRef(false)

  // Sync local if the row's unit changes from outside this cell.
  useEffect(() => {
    setLocal(item.unit)
    initialRef.current = item.unit
  }, [item.unit])

  const commit = (v: string) => {
    if (v !== initialRef.current) {
      initialRef.current = v
      onUnitChange(item.id, v)
    }
  }

  return (
    <Combobox<string>
      value={item.unit || null}
      onChange={(u) => { if (u == null) return; justSelectedRef.current = true; setLocal(u); commit(u) }}
      options={UNIT_PRESETS}
      getKey={u => u}
      getLabel={u => u}
      renderOption={u => u}
      inputValue={local}
      onInputChange={setLocal}
      onBlur={() => {
        if (justSelectedRef.current) { justSelectedRef.current = false; return }
        commit(local)
      }}
      placeholder="each"
      disabled={isLocked}
      allowFreeform
      emptyMessage="Keep typing to use as-is"
    />
  )
}

// ─── Arc B Slice 2 CP2: per-line tier-builder ────────────────────────────────
const PRICING_PILLS: Array<{ id: PricingType; label: string }> = [
  { id: 'flat', label: 'Flat' },
  { id: 'tiered', label: 'Tiered' },
  { id: 'volume', label: 'Volume' },
]

// Valid AND ascending: a usable schedule (mirrors isValidTiers + exactly one
// null "and above" final row + strictly ascending bounded upTo values). The
// non-blocking hint uses this; lineTotal independently falls back to flat on
// anything malformed.
function tiersValidAscending(tiers: PricingTier[] | null | undefined): boolean {
  if (!isValidTiers(tiers)) return false
  if (tiers.filter(t => t.upTo === null).length !== 1) return false
  const bounded = tiers.filter(t => t.upTo !== null).map(t => Number(t.upTo))
  for (let i = 1; i < bounded.length; i++) if (!(bounded[i] > bounded[i - 1])) return false
  return true
}

// The expandable sub-panel under a line: pricing-type pills + the {upTo, price}
// tier list with a fixed "and above" final row. Setting pricing_type/tiers via
// `update` recomputes the line total live (updateLineItem → lineTotal).
function TierPanel({ item, isLocked, update }: {
  item: LineItem
  isLocked: boolean
  update: (field: keyof LineItem, value: string | number | PricingType | PricingTier[] | null) => void
}) {
  const pricingType: PricingType = (item.pricing_type as PricingType) || 'flat'
  const tiers = item.tiers ?? []
  const showList = pricingType === 'tiered' || pricingType === 'volume'

  const setType = (type: PricingType) => {
    update('pricing_type', type)
    // Seed a valid 1-tier "and above" schedule from the current unit price when
    // switching to tiered/volume with no usable tiers yet. (flat keeps tiers
    // as-is — lineTotal ignores them for flat.)
    if ((type === 'tiered' || type === 'volume') && !isValidTiers(item.tiers)) {
      update('tiers', [{ upTo: null, price: Number(item.unit_price) || 0 }])
    }
  }
  const setTier = (idx: number, field: 'upTo' | 'price', raw: string) => {
    const v = parseFloat(raw)
    const num = Number.isFinite(v) ? v : 0
    update('tiers', tiers.map((t, i) => i === idx ? { ...t, [field]: num } : t))
  }
  const addTier = () => {
    const finalIdx = tiers.findIndex(t => t.upTo === null)
    const at = finalIdx === -1 ? tiers.length : finalIdx
    const prev = at > 0 ? tiers[at - 1] : null
    const defUpTo = prev && prev.upTo != null ? Number(prev.upTo) + 10 : 10
    const price = prev ? prev.price : (Number(item.unit_price) || 0)
    update('tiers', [...tiers.slice(0, at), { upTo: defUpTo, price }, ...tiers.slice(at)])
  }
  const removeTier = (idx: number) => update('tiers', tiers.filter((_, i) => i !== idx))

  const summary = tiers
    .map(t => t.upTo === null ? `and above: ${formatCurrency(t.price)}` : `up to ${t.upTo}: ${formatCurrency(t.price)}`)
    .join(', ')

  const tierInput: React.CSSProperties = {
    padding: '2px 6px', background: 'var(--field-bg)', border: '1px solid var(--border-default)',
    borderRadius: 5, fontSize: 12, color: 'var(--ink-primary)', outline: 'none',
  }

  return (
    <div style={{ padding: '10px 14px 12px 32px', background: 'var(--hover-bg)', border: '1px solid var(--border-subtle)', borderRadius: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
      {/* pricing-type pills */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', minWidth: 56 }}>Pricing</span>
        {isLocked ? (
          <span style={{ fontSize: 12, color: 'var(--ink-secondary)', textTransform: 'capitalize' }}>{pricingType}</span>
        ) : (
          <div style={{ display: 'flex', gap: 2, padding: 2, background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 6 }}>
            {PRICING_PILLS.map(opt => {
              const active = pricingType === opt.id
              return (
                <button key={opt.id} type="button" onClick={() => setType(opt.id)} aria-pressed={active}
                  style={{ padding: '2px 10px', fontSize: 11, fontWeight: 700, borderRadius: 4, cursor: 'pointer', background: active ? 'var(--accent)' : 'transparent', color: active ? 'var(--accent-ink)' : 'var(--ink-tertiary)', border: 'none' }}>
                  {opt.label}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Slice 3: per-line modifiers — flat fee + price multiplier. Apply to ALL
          pricing types (a flat OR tiered line can carry a trip fee + surcharge),
          so this row is NOT gated on pricing_type. total = base × mult + fee. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', minWidth: 56 }}>Modifiers</span>
        {isLocked ? (
          <span style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>
            {(item.line_fee || item.price_multiplier)
              ? [item.price_multiplier ? `×${item.price_multiplier}` : null, item.line_fee ? `Fee ${formatCurrency(Number(item.line_fee))}` : null].filter(Boolean).join(' · ')
              : '—'}
          </span>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>Fee $</span>
              <input type="number" min={0} step={0.01} value={item.line_fee ?? ''} placeholder="0"
                onChange={e => { const v = parseFloat(e.target.value); update('line_fee', Number.isFinite(v) && v > 0 ? v : null) }}
                style={{ ...tierInput, width: 64, textAlign: 'right' }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>×</span>
              <input type="number" min={0} step={0.01} value={item.price_multiplier ?? ''} placeholder="1"
                onChange={e => { const v = parseFloat(e.target.value); update('price_multiplier', Number.isFinite(v) && v > 0 ? v : null) }}
                style={{ ...tierInput, width: 64, textAlign: 'right' }} />
            </div>
          </>
        )}
      </div>

      {/* tier list (tiered/volume only) */}
      {showList && (isLocked ? (
        <div style={{ fontSize: 12, color: 'var(--ink-secondary)' }}>{summary || '—'}</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {tiers.map((t, idx) => (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {t.upTo === null ? (
                <span style={{ fontSize: 12, color: 'var(--ink-tertiary)', minWidth: 96 }}>and above</span>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 96 }}>
                  <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>up to</span>
                  <input type="number" min={0} value={t.upTo ?? ''} onChange={e => setTier(idx, 'upTo', e.target.value)}
                    style={{ ...tierInput, width: 56, textAlign: 'center' }} />
                </div>
              )}
              <span style={{ fontSize: 11, color: 'var(--ink-tertiary)' }}>$</span>
              <input type="number" min={0} step={0.01} value={t.price ?? ''} onChange={e => setTier(idx, 'price', e.target.value)}
                style={{ ...tierInput, width: 72, textAlign: 'right' }} />
              {t.upTo !== null && (
                <button type="button" onClick={() => removeTier(idx)} aria-label="Remove tier"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                  <Trash2 style={{ width: 12, height: 12 }} />
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={addTier}
            style={{ alignSelf: 'flex-start', marginTop: 2, padding: '3px 8px', background: 'none', border: '1px dashed var(--border-default)', borderRadius: 6, color: 'var(--ink-tertiary)', fontSize: 11, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
            <Plus style={{ width: 11, height: 11 }} /> Add tier
          </button>
          {!tiersValidAscending(item.tiers) && (
            <span style={{ fontSize: 11, color: 'var(--ink-tertiary)', fontStyle: 'italic' }}>
              Tiers should ascend; the total falls back to flat until fixed.
            </span>
          )}
        </div>
      ))}
    </div>
  )
}

export default function LineItemEditor({
  items, onItemsChange, isLocked, workspaceId,
}: Props) {
  const [showPicker, setShowPicker] = useState(false)
  const [expandedId, setExpandedId] = useState<string | null>(null) // Slice 2: per-line tier panel (single-open)

  // The transparent borderless input style — moved verbatim from the
  // parents' page-body `inp` (InvoiceDetail:800 / QuoteDetail:290). Used
  // only by the row inputs below.
  const inp: React.CSSProperties = {
    background: 'transparent', border: 'none', outline: 'none',
    fontSize: 13, color: 'var(--ink-primary)',
    width: '100%', padding: '4px 6px',
  }

  const addLineItem = () => {
    onItemsChange(prev => [...prev, {
      id: Date.now().toString(),
      name: '',
      description: '',
      qty: 1,
      unit: 'each',
      unit_price: 0,
      total: 0,
    }])
  }

  const updateLineItem = (itemId: string, field: keyof LineItem, value: string | number | PricingType | PricingTier[] | null) => {
    onItemsChange(prev => prev.map(item => {
      if (item.id !== itemId) return item
      const updated = { ...item, [field]: value }
      updated.total = lineTotal(updated) // Slice 2: flat → qty×unit_price; tiered/volume by schedule
      return updated
    }))
  }

  const removeLineItem = (itemId: string) => {
    onItemsChange(prev => prev.filter(i => i.id !== itemId))
  }

  return (
    <>
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 100px 100px 36px', gap: 8, padding: '8px 12px', marginBottom: 4 }}>
          {['Item', 'Qty', 'Unit', 'Unit price', 'Total', ''].map((h, i) => (
            <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</div>
          ))}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {items.map((item) => {
            const isTiered = item.pricing_type === 'tiered' || item.pricing_type === 'volume'
            const expanded = expandedId === item.id
            return (
            <React.Fragment key={item.id}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px 80px 100px 100px 36px', gap: 8, padding: '8px 12px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 10, alignItems: 'center' }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  {/* Slice 2: expand toggle reveals the pricing-type + tier builder.
                      Always shown (locked lines expand to VIEW the read-only summary). */}
                  <button type="button" onClick={() => setExpandedId(expanded ? null : item.id)}
                    aria-label={expanded ? 'Collapse pricing' : 'Expand pricing'} aria-expanded={expanded}
                    style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center' }}>
                    {expanded ? <ChevronDown style={{ width: 13, height: 13 }} /> : <ChevronRight style={{ width: 13, height: 13 }} />}
                  </button>
                  <input value={item.name} onChange={e => updateLineItem(item.id, 'name', e.target.value)}
                    placeholder="Item name" disabled={isLocked}
                    autoComplete="off" data-1p-ignore data-form-type="other"
                    style={{ ...inp, fontWeight: 600 }} />
                </div>
                <input value={item.description} onChange={e => updateLineItem(item.id, 'description', e.target.value)}
                  placeholder="Description (optional)" disabled={isLocked}
                  autoComplete="off" data-1p-ignore data-form-type="other"
                  style={{ ...inp, fontSize: 11, color: 'var(--ink-tertiary)', marginTop: 2 }} />
              </div>
              <input type="number" value={item.qty} onChange={e => updateLineItem(item.id, 'qty', parseFloat(e.target.value) || 0)}
                disabled={isLocked} style={{ ...inp, textAlign: 'center' }} />
              <UnitCell item={item} isLocked={isLocked} onUnitChange={(id, v) => updateLineItem(id, 'unit', v)} />
              {/* Slice 2: unit-price is inert for tiered/volume (price comes from the tiers). */}
              {isTiered ? (
                <span style={{ ...inp, textAlign: 'right', color: 'var(--ink-tertiary)', fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.pricing_type}</span>
              ) : (
                <input type="number" value={item.unit_price} onChange={e => updateLineItem(item.id, 'unit_price', parseFloat(e.target.value) || 0)}
                  disabled={isLocked} style={{ ...inp, textAlign: 'right' }} />
              )}
              <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: 0, textAlign: 'right' }}>{formatCurrency(lineTotal(item))}</p>
              {!isLocked && (
                <button onClick={() => removeLineItem(item.id)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--ink-tertiary)', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'var(--danger)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
                  <Trash2 style={{ width: 13, height: 13 }} />
                </button>
              )}
            </div>
            {expanded && (
              <TierPanel item={item} isLocked={isLocked}
                update={(field, value) => updateLineItem(item.id, field, value)} />
            )}
            </React.Fragment>
            )
          })}
        </div>

        {!isLocked && (
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={() => setShowPicker(true)}
              style={{ flex: 1, padding: '10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 10, color: 'var(--accent-text)', fontSize: 13, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Package style={{ width: 14, height: 14 }} /> From catalog
            </button>
            <button onClick={addLineItem}
              style={{ flex: 1, padding: '10px', background: 'none', border: '1px dashed var(--border-default)', borderRadius: 10, color: 'var(--ink-tertiary)', fontSize: 13, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Plus style={{ width: 14, height: 14 }} /> Add blank line
            </button>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showPicker && (
          <ProductPicker
            workspaceId={workspaceId}
            onSelect={(p) => {
              onItemsChange(prev => [...prev, {
                id: Date.now().toString(),
                product_id: p.id,
                name: p.name,
                description: p.description || '',
                qty: 1,
                unit: p.unit || 'unit',
                unit_price: p.unit_price || 0,
                total: p.unit_price || 0,
              }])
            }}
            onClose={() => setShowPicker(false)}
          />
        )}
      </AnimatePresence>
    </>
  )
}
